import React from "react";
import { Helmet } from "react-helmet";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, BookOpen, GraduationCap, BarChart3, FileText, Mail, Download } from "lucide-react";

const WhitePapersResearch = () => {
  const [email, setEmail] = React.useState("");

  const handleNotifyMe = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement email subscription
    console.log("Email subscription:", email);
    setEmail("");
  };

  const upcomingPapers = [
    {
      title: "AI in Healthcare: Clinical Validation and Outcomes",
      description: "Comprehensive analysis of AI implementation in clinical settings with evidence-based outcomes",
      category: "Clinical Research",
      icon: GraduationCap,
      timeline: "Q3 2025"
    },
    {
      title: "The Economics of Healthcare AI: ROI and Cost-Benefit Analysis",
      description: "Financial impact assessment of AI adoption in healthcare organizations",
      category: "Healthcare Economics", 
      icon: BarChart3,
      timeline: "Q3 2025"
    },
    {
      title: "Patient Safety Through Predictive Analytics",
      description: "Research on using AI for early detection and prevention of adverse events",
      category: "Patient Safety",
      icon: FileText,
      timeline: "Q4 2025"
    }
  ];

  const researchAreas = [
    "Clinical Decision Support Systems",
    "Predictive Analytics in Healthcare",
    "AI-Powered Diagnostic Tools",
    "Healthcare Workflow Optimization",
    "Patient Monitoring Technologies",
    "Healthcare Data Privacy & Security"
  ];

  return (
    <PageContainer>
      <Helmet>
        <title>White Papers & Research - Coming Soon | Healthcare AI Research</title>
        <meta name="description" content="Access cutting-edge research and white papers on AI in healthcare. Clinical studies, outcomes data, and evidence-based insights coming soon." />
        <link rel="canonical" href="/resources/white-papers-research" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6">
              <Clock className="h-8 w-8 text-primary mr-3" />
              <span className="text-primary font-semibold">Research Library Coming Soon</span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              Evidence-Based
              <span className="block bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                Healthcare AI Research
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Access peer-reviewed research, clinical studies, and comprehensive white papers that demonstrate 
              the efficacy and impact of AI in healthcare settings.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <form onSubmit={handleNotifyMe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email for research updates"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-w-[350px]"
                  required
                />
                <Button type="submit" className="px-6">
                  <Mail className="h-4 w-4 mr-2" />
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Research Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Upcoming Publications</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our research team is preparing comprehensive studies on AI implementation, 
                clinical outcomes, and healthcare transformation.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {upcomingPapers.map((paper, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <paper.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-primary">{paper.category}</div>
                        <div className="text-sm text-muted-foreground">{paper.timeline}</div>
                      </div>
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      {paper.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {paper.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Download className="h-4 w-4 mr-2" />
                      Download When Available
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">Our Research Focus</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Our interdisciplinary research team collaborates with leading healthcare institutions 
                  to advance the understanding and application of AI in clinical practice.
                </p>
                
                <div className="grid grid-cols-1 gap-4">
                  {researchAreas.map((area, index) => (
                    <div key={index} className="flex items-center p-4 bg-muted/30 rounded-lg">
                      <BookOpen className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="font-medium">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-6">What You'll Find</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <div className="font-semibold">Peer-Reviewed Studies</div>
                      <div className="text-sm text-muted-foreground">Published in leading medical journals</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <div className="font-semibold">Clinical Validation Data</div>
                      <div className="text-sm text-muted-foreground">Real-world evidence and outcomes</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <div className="font-semibold">Implementation Guides</div>
                      <div className="text-sm text-muted-foreground">Best practices for healthcare adoption</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <div>
                      <div className="font-semibold">Regulatory Insights</div>
                      <div className="text-sm text-muted-foreground">FDA compliance and safety standards</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Informed on Latest Research</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Join researchers, clinicians, and healthcare leaders who rely on our evidence-based insights 
              to guide AI implementation decisions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-8">
                <Mail className="h-5 w-5 mr-2" />
                Get Research Updates
              </Button>
              <Button variant="outline" size="lg" className="px-8">
                <GraduationCap className="h-5 w-5 mr-2" />
                Academic Partnerships
              </Button>
            </div>
          </div>
        </div>
      </section>
    </PageContainer>
  );
};

export default WhitePapersResearch;